import { Controller } from '@hotwired/stimulus'

const INDEX_PLACEHOLDER = '__INDEX__'

export default class NestedRecordsController extends Controller<HTMLElement> {
  declare readonly addButtonTarget: HTMLButtonElement
  declare readonly hasAddButtonTarget: boolean
  declare readonly hasSuspendToggleTarget: boolean
  declare readonly hasTemplateTarget: boolean
  declare readonly rowsTarget: HTMLElement
  declare readonly rowTargets: HTMLElement[]
  declare readonly suspendToggleTarget: HTMLInputElement
  declare readonly templateTarget: HTMLTemplateElement

  static targets = ['addButton', 'row', 'rows', 'suspendToggle', 'template']

  #nextIndex = 0

  // `:review` mode renders the add button already disabled, and suspension
  // syncing must not undo that — so its starting state is captured before
  // `#syncSuspension` runs.
  #addButtonLocked = false

  // Getters

  get visibleRowCount() {
    return this.rowTargets.filter(row => !row.hidden).length
  }

  // Lifecycle methods

  connect() {
    this.#nextIndex = this.rowTargets.length
    this.#addButtonLocked = this.hasAddButtonTarget && this.addButtonTarget.disabled
    this.#syncSuspension()
    this.#announce()
    this.dispatch('connect')
  }

  // Public methods

  add(event: Event) {
    event.preventDefault()

    if (!this.hasTemplateTarget) return

    this.rowsTarget.appendChild(this.#buildRow(this.#nextIndex++))
    this.#focusLastRow()
    this.#announce()
  }

  /**
   * Liquidating an Account sells every position, so it can't also carry
   * sell trades of its own. The rows are "suspended" rather than removed;
   * a.k.a. they are hidden and marked for destruction. However, unchecking
   * "Liquidate Account" restores them again. Nothing is actually destroyed
   * until the Submission is saved, and by then every row posts `_destroy`,
   * which is what `TradeRequestSubmission#remove_applicable_sell_trades` waits for.
   */
  suspendWhenChecked() {
    this.#syncSuspension()
    this.#announce()
  }

  remove(event: Event) {
    event.preventDefault()

    const row = (event.target as HTMLElement).closest<HTMLElement>('[data-nested-records-target="row"]')

    if (row) this.#removeRow(row)

    this.#announce()
  }

  removeAll(event?: Event) {
    event?.preventDefault()

    for (const row of this.rowTargets) {
      this.#removeRow(row)
    }

    this.#announce()
  }

  // Private methods

  #announce() {
    this.dispatch('changed', { detail: { count: this.visibleRowCount } })
  }

  // The `hidden` property on its own isn't enough: a Tailwind display utility
  // on the element outranks the browser's `[hidden]` rule, so a row carrying
  // `flex` stays on screen with the property set. The class does the hiding.
  //
  // The property is set alongside it because it keeps the element out of the
  // accessibility tree, and because `visibleRowCount` and the suspend/restore
  // guards read it.
  #hide(element: HTMLElement) {
    element.hidden = true
    element.classList.add('hidden')
  }

  #show(element: HTMLElement) {
    element.hidden = false
    element.classList.remove('hidden')
  }

  #buildRow(index: number) {
    const row = this.templateTarget.content.cloneNode(true) as DocumentFragment

    row.querySelectorAll('*').forEach(element => {
      for (const attribute of Array.from(element.attributes)) {
        if (!attribute.value.includes(INDEX_PLACEHOLDER)) continue

        element.setAttribute(attribute.name, attribute.value.replaceAll(INDEX_PLACEHOLDER, String(index)))
      }
    })

    return row
  }

  #syncSuspension() {
    if (!this.hasSuspendToggleTarget) return

    const suspended = this.suspendToggleTarget.checked

    for (const row of this.rowTargets) {
      suspended ? this.#suspendRow(row) : this.#restoreRow(row)
    }

    if (this.hasAddButtonTarget) this.#toggleAddButton(suspended)
  }

  #toggleAddButton(suspended: boolean) {
    suspended ? this.#hide(this.addButtonTarget) : this.#show(this.addButtonTarget)

    if (this.#addButtonLocked) return

    this.addButtonTarget.disabled = suspended
  }

  #suspendRow(row: HTMLElement) {
    if (row.hidden) return

    row.dataset.nestedRecordsSuspended = 'true'
    this.#deactivate(row)
  }

  #restoreRow(row: HTMLElement) {
    if (row.dataset.nestedRecordsSuspended !== 'true') return

    delete row.dataset.nestedRecordsSuspended
    this.#reactivate(row)
  }

  /**
   * Like `#removeRow`, but the row stays in the DOM so it can be restored. A
   * saved row still posts its id and destruction flag; an unsaved row has no
   * record behind it, so nothing it holds needs to post at all.
   */
  #deactivate(row: HTMLElement) {
    const destroy = row.querySelector<HTMLInputElement>('input[name$="[_destroy]"]')
    const id = row.querySelector<HTMLInputElement>('input[name$="[id]"]')
    const saved = Boolean(id?.value)

    if (saved && destroy) destroy.value = '1'

    this.#hide(row)

    row.querySelectorAll<HTMLInputElement>('input, select, textarea').forEach(field => {
      if (saved && (field === destroy || field === id)) return

      // Already disabled. `:review` mode renders every field this way,
      // and those have to stay disbled when the row is restored.
      if (field.disabled) return

      field.disabled = true
      field.dataset.nestedRecordsDisabled = 'true'
    })
  }

  #reactivate(row: HTMLElement) {
    const destroy = row.querySelector<HTMLInputElement>('input[name$="[_destroy]"]')

    if (destroy) destroy.value = '0'

    this.#show(row)

    row.querySelectorAll<HTMLInputElement>('[data-nested-records-disabled]').forEach(field => {
      field.disabled = false
      delete field.dataset.nestedRecordsDisabled
    })
  }

  #focusLastRow() {
    const row = this.rowTargets[this.rowTargets.length - 1]

    row?.querySelector<HTMLInputElement>('input:not([type="hidden"]), select, textarea')?.focus()
  }

  /**
   * A row the server rendered has a saved record in the DB, so it has to be
   * sent back marked for destruction. A row added in the browser is not
   * persisted yet and can simply be yeeted.
   */
  #removeRow(row: HTMLElement) {
    const destroy = row.querySelector<HTMLInputElement>('input[name$="[_destroy]"]')
    const id = row.querySelector<HTMLInputElement>('input[name$="[id]"]')

    if (!destroy || !id?.value) {
      row.remove()
      return
    }

    destroy.value = '1'
    this.#hide(row)

    // Everything except the id and the destruction flag, which still have to
    // post for the record to be destroyed.
    row.querySelectorAll<HTMLInputElement>('input, select, textarea').forEach(field => {
      if (field !== destroy && field !== id) field.disabled = true
    })
  }
}
